import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { Language } from '@/types';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

// timecaps.ru — русскоязычная версия, всё остальное (timecaps.life) — международная.
const DEFAULT_LANGUAGE_BY_DOMAIN: Record<string, Language> = {
  '.ru': 'ru',
};
const FALLBACK_LANGUAGE: Language = 'en';

// Храним только язык, который пользователь выбрал сам; иначе язык задаёт домен.
// Старый ключ 'timecaps_language' не читаем: туда при каждом визите записывался
// язык по умолчанию, и отличить его от осознанного выбора нельзя.
const LANGUAGE_STORAGE_KEY = 'timecaps_language_choice';

function getDefaultLanguage(): Language {
  const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (
    savedLanguage &&
    (savedLanguage === 'ru' || savedLanguage === 'en' || savedLanguage === 'fr')
  ) {
    return savedLanguage as Language;
  }
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';
  for (const [suffix, lang] of Object.entries(DEFAULT_LANGUAGE_BY_DOMAIN)) {
    if (hostname.endsWith(suffix)) return lang;
  }
  return FALLBACK_LANGUAGE;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  const [language, setLanguageState] = useState<Language>(getDefaultLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (newLanguage: Language) => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, newLanguage);
    setLanguageState(newLanguage);
  };

  const toggleLanguage = () => {
    setLanguage(language === 'ru' ? 'en' : 'ru');
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
